/**
 * Now (docs/ux/project-tab-spec.md §1.9): what every agent is doing, across projects. The board
 * (grouped by attention or project, keyboard triage, bulk actions) with the session on the right.
 */
import { useChildMatches, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { Button } from '@viko/transcript-ui'
import { MessageCircleQuestion, Play } from 'lucide-react'
import { useState } from 'react'
import { Split } from '../components/AppShell.js'
import { useAppUi } from '../components/app-ui.js'
import { BuildView, FeedbackSheet, HubCard } from '../components/HubCards.js'
import { InboxInspector } from '../components/inbox/InboxInspector.js'
import { MachineAccessBanner } from '../components/MachineAccess.js'
import { NowBoard } from '../components/now/NowBoard.js'
import { SessionView } from '../components/SessionView.js'
import { Empty, errorText, PageHead } from '../components/ui.js'
import { useBackend } from '../lib/backend.js'
import { useReturnFocus } from '../lib/focus-return.js'
import { useInbox, useInboxRanked, useProjects } from '../lib/queries.js'
import { shortcut } from '../lib/shortcut.js'
import type { BuildCard, StatusAnswer } from '../lib/types.js'

export function NowPage() {
  const child = useChildMatches()
  const params = useParams({ strict: false }) as { runId?: string }
  const search = useSearch({ strict: false }) as { i?: string; intent?: 'reply' | 'delegate' }
  const nav = useNavigate()
  // Closing the session or the decision puts focus back on the row that opened it.
  useReturnFocus(params.runId ? `run:${params.runId}` : search.i)
  const closeDecision = () => void nav({ to: '/now', search: {} as never })
  return (
    <Split
      collapseWhenEmpty
      hasDetail={child.length > 0 || !!search.i}
      list={<NowList current={params.runId} currentDecision={search.i} />}
      detail={
        params.runId ? (
          <SessionView
            key={params.runId}
            runId={params.runId}
            onBack={() => void nav({ to: '/now' })}
          />
        ) : search.i ? (
          // The decision opens here, in place: Now keeps its list and your place in it.
          <InboxInspector
            key={search.i}
            inspectKey={search.i}
            {...(search.intent ? { intent: search.intent } : {})}
            onClose={closeDecision}
            onDone={closeDecision}
            backLabel="Back to Now"
          />
        ) : null
      }
      fallback={
        <Empty center title="Pick a session">
          Select a run on the left, or press {shortcut('k')} to jump anywhere.
        </Empty>
      }
    />
  )
}

function NowList({ current, currentDecision }: { current?: string; currentDecision?: string }) {
  const ui = useAppUi()
  const nav = useNavigate()
  return (
    <>
      <PageHead title="Now">
        <Button
          variant="primary"
          size="sm"
          icon={<Play size={14} />}
          onClick={() => ui.openStart()}
        >
          New session
        </Button>
      </PageHead>
      <StatusAsk />
      <MachineAccessBanner />
      <NowBoard
        {...(current ? { current } : {})}
        {...(currentDecision ? { currentDecision } : {})}
        onOpen={(runId) => void nav({ to: '/now/$runId', params: { runId } })}
        onClose={() => void nav({ to: '/now', search: {} as never })}
        onOpenDecision={(i, intent) =>
          void nav({ to: '/now', search: { i, ...(intent ? { intent } : {}) } as never })
        }
      />
      <ShipStrip />
    </>
  )
}

/** "Is the jump fix done?" → deterministic status answer from the hub (no LLM). */
function StatusAsk() {
  const b = useBackend()
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState(false)
  const [answer, setAnswer] = useState<StatusAnswer | null>(null)
  const [error, setError] = useState<string | null>(null)
  const nav = useNavigate()
  // The hub answers from plaintext records, which encrypted accounts do not have (V08).
  if (b.content) return null
  const ask = async () => {
    if (!q.trim()) return
    setBusy(true)
    setError(null)
    try {
      setAnswer(await b.api.askStatus(q.trim()))
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }
  const run = answer?.refs.find((r) => r.kind === 'run')
  return (
    <div className="px-4 pb-3">
      <form
        className="statusask"
        role="search"
        aria-label="Ask about status"
        onSubmit={(e) => {
          e.preventDefault()
          void ask()
        }}
      >
        <MessageCircleQuestion size={15} className="flex-none text-text-3" aria-hidden="true" />
        <input
          className="statusask__input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Ask: is the jump fix landed? what's blocked?"
          aria-label="Status question"
        />
        <button
          type="submit"
          className="vk-btn vk-btn--ghost vk-btn--sm"
          disabled={busy || !q.trim()}
        >
          {busy ? 'Asking…' : 'Ask'}
        </button>
      </form>
      {answer || error ? (
        <div className="statusask__answer" role="status">
          {error ? (
            <span className="text-status-fail">{error}</span>
          ) : answer?.answer ? (
            <span>{answer.answer}</span>
          ) : (
            <span className="text-text-2">
              I can’t answer that from the records alone yet. Try naming a task or workstream.
            </span>
          )}
          {run ? (
            <button
              type="button"
              className="vk-btn vk-btn--ghost vk-btn--sm"
              onClick={() => void nav({ to: '/now/$runId', params: { runId: run.id } })}
            >
              Open session
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** Ship? taps waiting on a release lane and fresh builds, above the runs. */
function ShipStrip() {
  const inbox = useInbox()
  const ranked = useInboxRanked()
  const projects = useProjects()
  const [feedback, setFeedback] = useState<BuildCard | null>(null)
  const ships = (inbox.data ?? []).filter((i) => i.category === 'ship-tap')
  const builds = (ranked.data?.digest ?? [])
    .map((d) => ({ d, card: d.body as BuildCard | null }))
    .filter((x) => x.card?.buildId)
    .slice(0, 2)
  if (!ships.length && !builds.length) return null
  const name = (id: string) => projects.data?.find((p) => p.id === id)?.name
  return (
    <section aria-labelledby="now-ship" className="grid gap-3 px-4 pb-3">
      <h2 id="now-ship" className="vk-sr">
        Ship and builds
      </h2>
      {ships.map((i) => (
        <HubCard key={i.id} item={i} resolve={() => {}} onSnooze={() => {}} />
      ))}
      {builds.map(({ d, card }) => (
        <BuildView
          key={d.id}
          card={card as BuildCard}
          {...(name(d.projectId) ? { project: name(d.projectId) as string } : {})}
          onFeedback={() => setFeedback(card)}
        />
      ))}
      {feedback ? <FeedbackSheet build={feedback} onClose={() => setFeedback(null)} /> : null}
    </section>
  )
}
